<template>
  <div class="vx-row mb-4">
    <div class="vx-col md:w-2/5 w-full mb-2 md:mb-0">
      <v-select :dir="$vs.rtl ? 'rtl' : 'ltr'"
                :options="courseOutlineOptions"
                :placeholder="$t('video-reuse.filter-course-outline')"
                label="label"
                selectOnTab
                @search="fetchCourseOutlines"
                @input="onCourseOutlineChange"
                v-model="selectedCourseOutline">
        <template v-slot:no-options="{search, searching}">
          <template v-if="searching">
            {{ $t('general.no-result-for') }} <em>{{ search }}</em>.
          </template>
          <em style="opacity: 0.5;" v-else>{{ $t('video-reuse.filter-course-outline-hint') }}</em>
        </template>
      </v-select>
    </div>

    <div class="vx-col md:w-1/5 w-full mb-2 md:mb-0">
      <v-select :dir="$vs.rtl ? 'rtl' : 'ltr'"
                :options="vdoSetOptions"
                :placeholder="$t('video-reuse.filter-vdo-set')"
                :disabled="loading || !selectedCourseOutline"
                label="label"
                selectOnTab
                @input="onVdoSetChange"
                v-model="selectedVdoSet"/>
    </div>

    <div class="vx-col md:w-1/5 w-full mb-2 md:mb-0">
      <v-select :dir="$vs.rtl ? 'rtl' : 'ltr'"
                :options="vdoSetUnitOptions"
                :placeholder="$t('video-reuse.filter-unit')"
                :disabled="loading || !selectedVdoSet"
                label="label"
                selectOnTab
                @input="onVdoSetUnitChange"
                v-model="selectedVdoSetUnit"/>
    </div>

    <div class="vx-col md:w-1/5 w-full">
      <v-select :dir="$vs.rtl ? 'rtl' : 'ltr'"
                :options="vdoSetTopicOptions"
                :placeholder="$t('video-reuse.filter-topic')"
                :disabled="loading || !selectedVdoSetUnit"
                label="label"
                selectOnTab
                @input="onVdoSetTopicChange"
                v-model="selectedVdoSetTopic"/>
    </div>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import {createNamespacedHelpers} from 'vuex';

const {mapActions: mapCourseOutlineActions} = createNamespacedHelpers('CourseOutline');

// vue-select fires @search on every keystroke. Below this the match is the
// whole tenant and the list is useless -- same threshold as VdoSetList.vue.
const MIN_SEARCH_LENGTH = 2;

/**
 * Build one cascade level's options from the rows the dialog already loaded.
 * Walks in the order the server returned (set -> unit -> topic within a
 * course outline), keeps the first occurrence of each entity, and skips rows
 * where that level is null. Because the source is the result set, every
 * option is guaranteed to have a playable video behind it.
 * @param {Array} rows topics to derive from
 * @param {Function} pick row -> the entity at this level, or null
 * @param {Function} toLabel entity -> display string
 * @return {Array} [{id, label}]
 */
function uniqueOptions(rows, pick, toLabel) {
  const seen = new Set();
  const options = [];
  rows.forEach((row) => {
    const entity = pick(row);
    if (!entity || !entity.id || seen.has(entity.id)) return;
    seen.add(entity.id);
    options.push({id: entity.id, label: toLabel(entity)});
  });
  return options;
}

export default {
  name: 'BorrowVideoFilterBar',
  components: {vSelect},
  props: {
    // Rows the dialog has loaded for the selected course outline. The three
    // lower selects derive from these rather than querying their own option
    // lists, so a selection can never produce an empty table.
    topics: {type: Array, default: () => []},
    // True while the dialog is fetching. The lower selects lock; the course
    // outline select stays live so the admin can change their mind mid-fetch.
    loading: {type: Boolean, default: false},
  },
  data() {
    return {
      courseOutlineOptions: [],
      selectedCourseOutline: null,
      selectedVdoSet: null,
      selectedVdoSetUnit: null,
      selectedVdoSetTopic: null,
      // Discards a slower keystroke's response that lands after a newer one.
      searchSeq: 0,
    };
  },
  computed: {
    vdoSetOptions() {
      return uniqueOptions(
          this.topics,
          (row) => row.vdoSet,
          (vdoSet) => vdoSet.code || '');
    },
    vdoSetUnitOptions() {
      return uniqueOptions(
          this.rowsInSelectedVdoSet,
          (row) => row.vdoSetUnit,
          (unit) => `${unit.unitSeq == null ? '' : unit.unitSeq}. ${unit.nameLocal || ''}`.trim());
    },
    vdoSetTopicOptions() {
      const rows = this.selectedVdoSetUnit ?
        this.rowsInSelectedVdoSet.filter(
            (row) => row.vdoSetUnit && row.vdoSetUnit.id === this.selectedVdoSetUnit.id) :
        [];
      return uniqueOptions(
          rows,
          (row) => row,
          (row) => `${row.no == null ? '' : row.no}. ${row.nameLocal || ''}`.trim());
    },
    rowsInSelectedVdoSet() {
      if (!this.selectedVdoSet) return [];
      return this.topics.filter(
          (row) => row.vdoSet && row.vdoSet.id === this.selectedVdoSet.id);
    },
  },
  methods: {
    ...mapCourseOutlineActions({
      getCourseOutlinesByCodeOrName: 'getCourseOutlinesByCodeOrName',
    }),
    async fetchCourseOutlines(search, loading) {
      const keyword = (search || '').trim();
      // Bumped before the threshold check, not after: a fetch already in
      // flight must not repopulate the list the admin just emptied by
      // backspacing below the threshold.
      const seq = ++this.searchSeq;
      if (keyword.length < MIN_SEARCH_LENGTH) {
        this.courseOutlineOptions = [];
        // That bump orphaned any in-flight fetch, so its own finally will
        // skip loading(false) -- clear the spinner here instead.
        loading(false);
        return;
      }

      loading(true);
      try {
        const rows = await this.getCourseOutlinesByCodeOrName({
          codeOrName: keyword,
          limit: 20,
        });
        // A slower earlier keystroke must not overwrite a newer result.
        if (seq !== this.searchSeq) return;
        this.courseOutlineOptions = rows.map((row) => ({
          ...row,
          label: this.courseOutlineLabel(row),
        }));
      } finally {
        if (seq === this.searchSeq) loading(false);
      }
    },
    courseOutlineLabel(row) {
      const subjectName = row.subject ? row.subject.nameLocal || '' : '';
      const teacherName = row.teacher ?
        `${row.teacher.firstNameLocal || ''} ${row.teacher.lastNameLocal || ''}`.trim() : '';
      return `${row.subjectCode} ${subjectName} — ${row.teacherCode} ${teacherName}`.trim();
    },
    onCourseOutlineChange() {
      // Every level below is meaningless against a different outline.
      this.selectedVdoSet = null;
      this.selectedVdoSetUnit = null;
      this.selectedVdoSetTopic = null;
      this.$emit('course-outline-change', this.selectedCourseOutline);
      this.emitFilter();
    },
    onVdoSetChange() {
      this.selectedVdoSetUnit = null;
      this.selectedVdoSetTopic = null;
      this.emitFilter();
    },
    onVdoSetUnitChange() {
      this.selectedVdoSetTopic = null;
      this.emitFilter();
    },
    onVdoSetTopicChange() {
      this.emitFilter();
    },
    emitFilter() {
      this.$emit('filter-change', {
        vdoSetId: this.selectedVdoSet ? this.selectedVdoSet.id : null,
        vdoSetUnitId: this.selectedVdoSetUnit ? this.selectedVdoSetUnit.id : null,
        vdoSetTopicId: this.selectedVdoSetTopic ? this.selectedVdoSetTopic.id : null,
      });
    },
  },
};
</script>
